import { Children, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import Select, { components } from 'react-select';
import AppScrollArea from '../../../components/common/AppScrollArea';
import { getOrderListSelectStyles } from '../../../utils/selectStyles';
import { MAX_ADAPTIVE_VISIBLE_CHIPS, resolveChipDisplayMode } from './orderListMultiSelectChipLayout';

const readPageIsDark = () => typeof document !== 'undefined' && document.documentElement.getAttribute('data-bs-theme') === 'dark';

const ORDER_LIST_SELECT_MENU_MAX_HEIGHT = 232;
const WIDTH_MEASURE_EPSILON_PX = 6;
/** 默认参数禁止写 `= []`：每次渲染都是新引用，会拖垮 chip 同步 effect */
const EMPTY_ARRAY = Object.freeze([]);

const toArray = (value) => {
	if (Array.isArray(value)) return value;
	if (typeof value === 'string' && value.trim()) return [value.trim()];
	return [];
};

const toSelectOptionsFromValues = (values = []) => toArray(values).map((value) => ({ value, label: value }));

const matchFilterOption = (option, rawInput, filterOption) => {
	if (typeof filterOption !== 'function') return true;
	return filterOption(
		{
			label: option?.label,
			value: option?.value,
			data: option,
		},
		rawInput,
	);
};

const getVisibleOptions = (selectProps) => {
	const options = selectProps?.options ?? [];
	const inputValue = selectProps?.inputValue ?? '';
	const filterOption = selectProps?.filterOption;
	return options.filter((option) => matchFilterOption(option, inputValue, filterOption));
};

/** 打开菜单 / 搜索变更时冻结展示顺序，避免勾选即时重排导致 hover 闪跳 */
const buildMenuLock = (visibleOptions = [], selectedValues = [], pinSelectedToTop = true) => {
	const selectedSet = new Set(toArray(selectedValues).map(String));
	const order = [];
	const selected = [];
	const unselected = [];

	visibleOptions.forEach((option) => {
		const value = String(option.value);
		if (pinSelectedToTop && selectedSet.has(value)) selected.push(value);
		else unselected.push(value);
	});

	if (pinSelectedToTop && selected.length > 0) {
		order.push(...selected, ...unselected);
		return { order, pinCount: selected.length };
	}

	order.push(...unselected);
	return { order, pinCount: 0 };
};

const buildOrderedMenuChildren = (children, selectProps) => {
	const childByValue = new Map();
	Children.forEach(children, (child) => {
		const value = child?.props?.data?.value;
		if (value === undefined) return;
		childByValue.set(String(value), child);
	});

	const menuLock = selectProps?.menuLock;
	if (menuLock?.order?.length) {
		const pinnedIds = new Set(menuLock.order.slice(0, menuLock.pinCount).map(String));
		const seen = new Set();
		const ordered = [];

		menuLock.order.forEach((value) => {
			const key = String(value);
			const child = childByValue.get(key);
			if (!child || seen.has(key)) return;
			seen.add(key);
			ordered.push(child);
		});

		childByValue.forEach((child, key) => {
			if (seen.has(key)) return;
			seen.add(key);
			ordered.push(child);
		});

		if (menuLock.pinCount <= 0) {
			return { selected: [], unselected: ordered, hasDivider: false };
		}

		const selected = [];
		const unselected = [];
		ordered.forEach((child) => {
			const key = String(child?.props?.data?.value);
			if (pinnedIds.has(key)) selected.push(child);
			else unselected.push(child);
		});

		return {
			selected,
			unselected,
			hasDivider: selected.length > 0 && unselected.length > 0,
		};
	}

	const pinSelectedToTop = selectProps?.pinSelectedToTop ?? true;
	const options = selectProps?.options ?? [];
	const selectedSet = new Set((selectProps?.value ?? []).map((item) => String(item.value)));

	if (!pinSelectedToTop || selectedSet.size === 0) {
		const ordered = options.map((option) => childByValue.get(String(option.value))).filter(Boolean);
		return { selected: [], unselected: ordered, hasDivider: false };
	}

	const selected = [];
	const unselected = [];
	options.forEach((option) => {
		const child = childByValue.get(String(option.value));
		if (!child) return;
		if (selectedSet.has(String(option.value))) selected.push(child);
		else unselected.push(child);
	});

	return {
		selected,
		unselected,
		hasDivider: selected.length > 0 && unselected.length > 0,
	};
};

const createValueRenderer = (chipDisplayMode, maxVisibleChips) => (props) => {
	const { index, getValue } = props;
	const selectedCount = getValue().length;

	if (chipDisplayMode === 'summary') {
		if (index !== 0) return null;
		return (
			<div className='react-select__multi-value order-list-select-summary-chip'>
				<div className='react-select__multi-value__label'>已选 {selectedCount} 项</div>
			</div>
		);
	}

	if (index < maxVisibleChips) {
		return <components.MultiValue {...props} />;
	}
	if (index === maxVisibleChips && selectedCount > maxVisibleChips) {
		return (
			<div className='react-select__multi-value order-list-select-overflow-chip'>
				<div className='react-select__multi-value__label'>+{selectedCount - maxVisibleChips} 项</div>
			</div>
		);
	}
	return null;
};

const stopMenuCloseOnMouseDown = (event) => {
	event.preventDefault();
	event.stopPropagation();
};

const OrderListSelectMenuToolbar = ({ selectProps }) => {
	const visibleOptions = getVisibleOptions(selectProps);
	const selectedSet = new Set((selectProps?.value ?? []).map((item) => String(item.value)));
	const selectedVisibleCount = visibleOptions.reduce(
		(count, option) => (selectedSet.has(String(option.value)) ? count + 1 : count),
		0,
	);
	const visibleCount = visibleOptions.length;
	const allVisibleSelected = visibleCount > 0 && selectedVisibleCount === visibleCount;
	const hasVisibleSelection = selectedVisibleCount > 0;
	const totalSelected = selectedSet.size;
	const isSearching = Boolean(String(selectProps?.inputValue ?? '').trim());

	const handleSelectAllVisible = (event) => {
		stopMenuCloseOnMouseDown(event);
		selectProps?.onSelectAllVisible?.();
	};

	const handleClearVisible = (event) => {
		stopMenuCloseOnMouseDown(event);
		selectProps?.onClearVisible?.();
	};

	return (
		<div className='order-list-select-menu-toolbar' onMouseDown={stopMenuCloseOnMouseDown}>
			<div className='order-list-select-menu-toolbar__actions'>
				<button
					type='button'
					className='order-list-select-menu-toolbar__btn'
					disabled={visibleCount === 0 || allVisibleSelected}
					onMouseDown={handleSelectAllVisible}
				>
					{isSearching ? '全选当前' : '全选'}
				</button>
				<button
					type='button'
					className='order-list-select-menu-toolbar__btn'
					disabled={!hasVisibleSelection}
					onMouseDown={handleClearVisible}
				>
					{isSearching ? '清空当前' : '清空'}
				</button>
			</div>
			<span className='order-list-select-menu-toolbar__meta' aria-live='polite'>
				{isSearching ? `当前 ${selectedVisibleCount}/${visibleCount}` : `已选 ${totalSelected}`}
			</span>
		</div>
	);
};

const OrderListSelectMenuList = ({ children, innerProps, innerRef, maxHeight, selectProps }) => {
	const { className, ...restInnerProps } = innerProps ?? {};
	const menuMaxHeight = maxHeight ?? ORDER_LIST_SELECT_MENU_MAX_HEIGHT;
	const { selected, unselected, hasDivider } = buildOrderedMenuChildren(children, selectProps);
	const handleMenuMouseMove = selectProps.onMenuMouseMove;

	return (
		<>
			<OrderListSelectMenuToolbar selectProps={selectProps} />
			<AppScrollArea
				className='order-list-multi-select-scroll'
				maxHeight={menuMaxHeight}
				scrollableNodeProps={{
					ref: innerRef,
					onMouseMove: handleMenuMouseMove,
				}}
			>
				<div
					{...restInnerProps}
					className={[className, 'order-list-multi-select-menu-list'].filter(Boolean).join(' ')}
					onMouseMove={handleMenuMouseMove}
				>
					{selected}
					{hasDivider ? <div className='order-list-select-menu-divider' role='separator' aria-hidden='true' /> : null}
					{unselected}
				</div>
			</AppScrollArea>
		</>
	);
};

const OrderListSelectOption = (props) => {
	const { isSelected, label, data, isFocused, selectProps } = props;
	/** 鼠标高亮交给 CSS :hover；JS focus 仅保留给键盘，避免重渲/重排时闪烁 */
	const showFocused = isFocused && selectProps?.optionHighlightIntent === 'keyboard';
	const secondaryLabel = typeof data?.secondaryLabel === 'string' ? data.secondaryLabel.trim() : '';
	const hasSecondary = Boolean(secondaryLabel);

	return (
		<components.Option {...props} isFocused={showFocused}>
			<span
				className={[
					'order-list-select-option-row',
					hasSecondary ? 'order-list-select-option-row--stacked' : null,
					isSelected ? 'order-list-select-option-row--selected' : null,
				]
					.filter(Boolean)
					.join(' ')}
			>
				<span
					className={['order-list-select-option-check', isSelected ? 'order-list-select-option-check--checked' : null]
						.filter(Boolean)
						.join(' ')}
					aria-hidden='true'
				>
					{isSelected ? (
						<svg className='order-list-select-option-check__icon' viewBox='0 0 12 12' focusable='false'>
							<path d='M2.4 6.2 4.8 8.6 9.6 3.4' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' />
						</svg>
					) : null}
				</span>
				<span
					className={['order-list-select-option-label', hasSecondary ? 'order-list-select-option-label--with-secondary' : null]
						.filter(Boolean)
						.join(' ')}
				>
					{hasSecondary ? <span className='order-list-select-option-primary'>{label}</span> : label}
					{hasSecondary ? <span className='order-list-select-option-secondary'>{secondaryLabel}</span> : null}
				</span>
			</span>
		</components.Option>
	);
};

/** 始终占位，避免无选中时清除按钮出现导致控件宽度抖动 */
const OrderListSelectClearIndicator = (props) => {
	const hasValue = (props.getValue()?.length ?? 0) > 0;
	return (
		<components.ClearIndicator
			{...props}
			innerProps={{
				...props.innerProps,
				style: {
					...(props.innerProps?.style ?? {}),
					visibility: hasValue ? 'visible' : 'hidden',
					pointerEvents: hasValue ? 'auto' : 'none',
				},
			}}
		/>
	);
};

/** 下拉箭头容器：图标由 CSS 复用 form-select 同款 background-image */
const OrderListSelectDropdownIndicator = (props) => (
	<components.DropdownIndicator {...props}>
		<span className='order-list-select-chevron-icon' aria-hidden='true' />
	</components.DropdownIndicator>
);

const OrderListSelectIndicatorsContainer = ({ children, ...props }) => {
	const showEnterHint = Boolean(props.selectProps?.showEnterHint);
	const hasQuery = Boolean(String(props.selectProps?.inputValue ?? '').trim());
	return (
		<components.IndicatorsContainer {...props}>
			{showEnterHint && hasQuery ? (
				<kbd className='order-list-select-enter-badge' title='按 Enter 确认'>
					Enter
				</kbd>
			) : null}
			{children}
		</components.IndicatorsContainer>
	);
};

/** 默认：加载态仅在下拉菜单内展示，不在输入框右侧显示 spinner */
const OrderListSelectLoadingIndicatorHidden = () => null;

const OrderListSelectLoadingMessage = (props) => (
	<components.LoadingMessage {...props}>
		<span className='order-list-select-menu-loading small text-muted'>加载中，请稍候…</span>
	</components.LoadingMessage>
);

const measureValueAreaWidth = (root, menuIsOpen, isFocused) => {
	if (!root) return 0;
	const control = root.querySelector('.react-select__control');
	const indicators = root.querySelector('.react-select__indicators');
	if (!control) return root.clientWidth;

	const controlStyles = window.getComputedStyle(control);
	const paddingLeft = Number.parseFloat(controlStyles.paddingLeft) || 0;
	const controlWidth = control.clientWidth;
	const indicatorsWidth = indicators?.offsetWidth ?? 0;

	let inputReserve = 0;
	if (menuIsOpen || isFocused) {
		const inputContainer = root.querySelector('.react-select__input-container');
		inputReserve = inputContainer?.offsetWidth ?? 48;
	}

	return Math.max(0, controlWidth - indicatorsWidth - paddingLeft - inputReserve);
};

const OrderListMultiSelectFilter = ({
	label,
	inputId,
	values = EMPTY_ARRAY,
	options: optionItems,
	selected = EMPTY_ARRAY,
	onChange,
	isLoading = false,
	placeholder = '请选择/输入',
	maxVisibleChips,
	pinSelectedToTop = true,
	menuMinWidth,
	showLoadingIndicator = false,
	menuInPlace = false,
	showEnterHint = false,
}) => {
	const reduxIsDark = useSelector((state) => state.config?.isDark);
	const isDark = reduxIsDark || readPageIsDark();
	const containerRef = useRef(null);
	const resizeFrameRef = useRef(null);
	const [menuIsOpen, setMenuIsOpen] = useState(false);
	const [isFocused, setIsFocused] = useState(false);
	const [valueAreaWidth, setValueAreaWidth] = useState(0);
	const [displayChipMode, setDisplayChipMode] = useState({ mode: 'empty', visible: 0 });
	const [inputValue, setInputValue] = useState('');
	const [menuLock, setMenuLock] = useState(null);
	const [optionHighlightIntent, setOptionHighlightIntent] = useState('mouse');
	const optionHighlightIntentRef = useRef('mouse');

	const maxChipCap = typeof maxVisibleChips === 'number' ? maxVisibleChips : MAX_ADAPTIVE_VISIBLE_CHIPS;

	const setHighlightIntent = useCallback((next) => {
		if (optionHighlightIntentRef.current === next) return;
		optionHighlightIntentRef.current = next;
		setOptionHighlightIntent(next);
	}, []);

	const updateValueAreaWidth = useCallback(() => {
		if (resizeFrameRef.current != null) {
			cancelAnimationFrame(resizeFrameRef.current);
		}
		resizeFrameRef.current = requestAnimationFrame(() => {
			resizeFrameRef.current = null;
			const next = measureValueAreaWidth(containerRef.current, menuIsOpen, isFocused);
			setValueAreaWidth((prev) => (Math.abs(prev - next) < WIDTH_MEASURE_EPSILON_PX ? prev : next));
		});
	}, [isFocused, menuIsOpen]);

	useEffect(() => {
		const root = containerRef.current;
		if (!root) return undefined;

		updateValueAreaWidth();
		const observer = new ResizeObserver(() => updateValueAreaWidth());
		observer.observe(root);
		return () => {
			observer.disconnect();
			if (resizeFrameRef.current != null) {
				cancelAnimationFrame(resizeFrameRef.current);
			}
		};
	}, [updateValueAreaWidth]);

	const options = useMemo(() => {
		if (Array.isArray(optionItems) && optionItems.length > 0) {
			return optionItems.map((item) => ({
				value: String(item.value),
				label: item.label ?? String(item.value),
				...(item.secondaryLabel ? { secondaryLabel: String(item.secondaryLabel) } : {}),
			}));
		}
		return toSelectOptionsFromValues(values);
	}, [optionItems, values]);

	const selectedValues = useMemo(() => toArray(selected).map(String), [selected]);
	const selectedOptions = useMemo(
		() => selectedValues.map((value) => options.find((item) => String(item.value) === value) ?? { value, label: value }),
		[options, selectedValues],
	);

	const filterOption = useCallback((option, rawInput) => {
		const query = String(rawInput ?? '')
			.trim()
			.toLowerCase();
		if (!query) return true;
		const optionLabel = String(option?.label ?? '').toLowerCase();
		const secondary = String(option?.data?.secondaryLabel ?? '').toLowerCase();
		return optionLabel.includes(query) || secondary.includes(query);
	}, []);

	const optionsFingerprint = useMemo(() => options.map((option) => String(option.value)).join('\0'), [options]);

	const refreshMenuLock = useCallback(
		(rawInput, selectedForLock = selectedValues) => {
			const visible = options.filter((option) => matchFilterOption(option, rawInput, filterOption));
			setMenuLock(buildMenuLock(visible, selectedForLock, pinSelectedToTop));
		},
		[filterOption, options, pinSelectedToTop, selectedValues],
	);

	const resolvedChipMode = useMemo(
		() => resolveChipDisplayMode(valueAreaWidth, selectedOptions, maxChipCap),
		[valueAreaWidth, selectedOptions, maxChipCap],
	);

	useEffect(() => {
		if (menuIsOpen) return;
		setDisplayChipMode((prev) =>
			prev.mode === resolvedChipMode.mode && prev.visible === resolvedChipMode.visible ? prev : resolvedChipMode,
		);
	}, [menuIsOpen, resolvedChipMode]);

	useEffect(() => {
		updateValueAreaWidth();
	}, [isFocused, menuIsOpen, updateValueAreaWidth]);

	/** 菜单打开期间选项异步到位时，只追加新值，不因勾选重排 */
	useEffect(() => {
		if (!menuIsOpen) return;
		setMenuLock((prev) => {
			const visible = options.filter((option) => matchFilterOption(option, inputValue, filterOption));
			if (!prev) return buildMenuLock(visible, selectedValues, pinSelectedToTop);

			const visibleValues = visible.map((option) => String(option.value));
			const visibleSet = new Set(visibleValues);
			const kept = prev.order.filter((value) => visibleSet.has(value));
			const keptSet = new Set(kept);
			const appended = visibleValues.filter((value) => !keptSet.has(value));
			if (kept.length === prev.order.length && appended.length === 0) return prev;

			const prevPinned = new Set(prev.order.slice(0, prev.pinCount));
			const nextPinCount = kept.reduce((count, value) => (prevPinned.has(value) ? count + 1 : count), 0);
			return { order: [...kept, ...appended], pinCount: nextPinCount };
		});
		// selectedValues intentionally omitted: 勾选中不得重排
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [menuIsOpen, optionsFingerprint, inputValue, filterOption, pinSelectedToTop]);

	const effectiveChipMode = menuIsOpen ? displayChipMode : resolvedChipMode;
	const effectiveVisibleChips = effectiveChipMode.visible;

	const selectStyles = useMemo(() => {
		const base = getOrderListSelectStyles(isDark);
		/** inline 字段：菜单与输入框外壳同宽、左对齐 */
		const resolveInlineFieldRect = () => containerRef.current?.closest('.statistics-filter-field--inline')?.getBoundingClientRect() ?? null;
		const shouldMatchControlWidth = () => menuInPlace || Boolean(resolveInlineFieldRect());

		const menuPortal = (portalBase) => {
			const withZ = base.menuPortal(portalBase);
			const rect = resolveInlineFieldRect();
			if (!rect) return withZ;
			const menuWidth = rect.width;
			return {
				...withZ,
				left: rect.left,
				width: menuWidth,
				minWidth: menuWidth,
				maxWidth: menuWidth,
			};
		};

		const menu = (baseStyles, state) => {
			const merged = base.menu(baseStyles, state);
			if (shouldMatchControlWidth()) {
				return {
					...merged,
					width: '100%',
					minWidth: '100%',
					maxWidth: '100%',
				};
			}
			if (!menuMinWidth) return merged;
			return {
				...merged,
				minWidth: menuMinWidth,
				width: 'max-content',
				maxWidth: 'min(28rem, 92vw)',
			};
		};

		return {
			...base,
			menuPortal,
			menu,
			option: (baseStyles, state) => {
				const merged = base.option(baseStyles, state);
				if (!shouldMatchControlWidth()) return merged;
				return {
					...merged,
					height: 'auto',
					minHeight: merged.minHeight ?? undefined,
					overflow: 'visible',
					textOverflow: 'clip',
					whiteSpace: 'normal',
					maxWidth: '100%',
					wordBreak: 'break-word',
					overflowWrap: 'break-word',
				};
			},
		};
	}, [isDark, menuInPlace, menuMinWidth]);
	const selectComponents = useMemo(
		() => ({
			MultiValue: createValueRenderer(effectiveChipMode.mode, effectiveVisibleChips),
			MenuList: OrderListSelectMenuList,
			Option: OrderListSelectOption,
			DropdownIndicator: OrderListSelectDropdownIndicator,
			ClearIndicator: OrderListSelectClearIndicator,
			IndicatorsContainer: OrderListSelectIndicatorsContainer,
			LoadingIndicator: showLoadingIndicator ? components.LoadingIndicator : OrderListSelectLoadingIndicatorHidden,
			LoadingMessage: OrderListSelectLoadingMessage,
		}),
		[effectiveChipMode.mode, effectiveVisibleChips, showLoadingIndicator],
	);
	const selectClassNames = useMemo(
		() => ({
			menu: () => {
				const isInline = Boolean(containerRef.current?.closest('.statistics-filter-field--inline'));
				const matchField = menuInPlace || isInline;
				return [
					'order-list-multi-select-menu',
					'order-list-multi-select-menu--pointer-hover',
					matchField ? 'order-list-multi-select-menu--match-field' : 'order-list-multi-select-menu--fit',
					'mt-0',
					'p-0',
				].join(' ');
			},
		}),
		[menuInPlace],
	);

	const handleMenuOpen = useCallback(() => {
		setDisplayChipMode(resolvedChipMode);
		setHighlightIntent('mouse');
		refreshMenuLock(inputValue, selectedValues);
		setMenuIsOpen(true);
	}, [inputValue, refreshMenuLock, resolvedChipMode, selectedValues, setHighlightIntent]);

	const handleMenuClose = useCallback(() => {
		setMenuIsOpen(false);
		setInputValue('');
		setMenuLock(null);
		setHighlightIntent('mouse');
	}, [setHighlightIntent]);

	const handleMenuMouseMove = useCallback(() => {
		setHighlightIntent('mouse');
	}, [setHighlightIntent]);

	const handleSelectKeyDown = useCallback(
		(event) => {
			if (['ArrowDown', 'ArrowUp', 'Home', 'End', 'PageUp', 'PageDown'].includes(event.key)) {
				setHighlightIntent('keyboard');
			}
		},
		[setHighlightIntent],
	);

	const handleSelectStateReducer = useCallback((state, actionMeta) => {
		switch (actionMeta.action) {
			case 'select-option':
			case 'deselect-option':
				return { ...state, focusedOption: null };
			default:
				return state;
		}
	}, []);

	const handleFocus = useCallback(() => {
		setIsFocused(true);
	}, []);

	const handleBlur = useCallback(() => {
		setIsFocused(false);
	}, []);

	const handleInputChange = useCallback(
		(nextValue, { action }) => {
			switch (action) {
				case 'input-change':
					setInputValue(nextValue);
					refreshMenuLock(nextValue, selectedValues);
					break;
				case 'input-blur':
				case 'menu-close':
				case 'set-value':
					setInputValue('');
					break;
				default:
					break;
			}
		},
		[refreshMenuLock, selectedValues],
	);

	const handleChange = useCallback(
		(next) => {
			setHighlightIntent('mouse');
			onChange(next ? next.map((item) => item.value) : []);
		},
		[onChange, setHighlightIntent],
	);

	const handleSelectAllVisible = useCallback(() => {
		const visible = options.filter((option) => matchFilterOption(option, inputValue, filterOption));
		if (visible.length === 0) return;

		setHighlightIntent('mouse');
		const next = [...selectedValues];
		const selectedSet = new Set(next);
		visible.forEach((option) => {
			const value = String(option.value);
			if (!selectedSet.has(value)) {
				selectedSet.add(value);
				next.push(value);
			}
		});
		onChange(next);
	}, [filterOption, inputValue, onChange, options, selectedValues, setHighlightIntent]);

	const handleClearVisible = useCallback(() => {
		const visible = options.filter((option) => matchFilterOption(option, inputValue, filterOption));
		if (visible.length === 0) return;

		setHighlightIntent('mouse');
		const visibleSet = new Set(visible.map((option) => String(option.value)));
		onChange(selectedValues.filter((value) => !visibleSet.has(value)));
	}, [filterOption, inputValue, onChange, options, selectedValues, setHighlightIntent]);

	const resolvedInputId = inputId ?? `order-list-filter-${label}`;
	const isEmptyOptions = !isLoading && options.length === 0;
	const selectPlaceholder = isLoading ? '加载中，请稍候…' : isEmptyOptions ? '暂无选项' : placeholder;
	const isSearchActive = menuIsOpen || isFocused;
	const isSummaryMode = effectiveChipMode.mode === 'summary';
	const visibleChipCount = isSummaryMode ? (selectedValues.length > 0 ? 1 : 0) : Math.min(selectedValues.length, effectiveVisibleChips);
	const hasOverflow = !isSummaryMode && selectedValues.length > effectiveVisibleChips;
	const filterClassName = [
		'order-list-multi-select-filter',
		selectedValues.length > 0 ? 'order-list-multi-select-filter--has-value' : '',
		hasOverflow ? 'order-list-multi-select-filter--has-overflow' : '',
		isSummaryMode ? 'order-list-multi-select-filter--summary' : '',
		visibleChipCount === 1 ? 'order-list-multi-select-filter--one-chip' : '',
		maxChipCap === 1 ? 'order-list-multi-select-filter--single-chip' : '',
		menuIsOpen ? 'order-list-multi-select-filter--menu-open' : '',
		isFocused ? 'order-list-multi-select-filter--focused' : '',
		inputValue ? 'order-list-multi-select-filter--typing' : '',
		isSearchActive ? 'order-list-multi-select-filter--searchable' : '',
		isEmptyOptions ? 'order-list-multi-select-filter--empty' : '',
		isLoading ? 'order-list-multi-select-filter--loading' : '',
	]
		.filter(Boolean)
		.join(' ');

	return (
		<div ref={containerRef} className='order-list-multi-select-filter-wrap'>
			<Select
				inputId={resolvedInputId}
				name={resolvedInputId}
				isMulti
				isSearchable
				isClearable
				isLoading={isLoading}
				closeMenuOnSelect={false}
				blurInputOnSelect={false}
				hideSelectedOptions={false}
				inputValue={inputValue}
				maxMenuHeight={ORDER_LIST_SELECT_MENU_MAX_HEIGHT}
				placeholder={selectPlaceholder}
				options={options}
				value={selectedOptions}
				filterOption={filterOption}
				pinSelectedToTop={pinSelectedToTop}
				menuLock={menuLock}
				optionHighlightIntent={optionHighlightIntent}
				showEnterHint={showEnterHint}
				onMenuMouseMove={handleMenuMouseMove}
				onSelectAllVisible={handleSelectAllVisible}
				onClearVisible={handleClearVisible}
				stateReducer={handleSelectStateReducer}
				scrollToFocusedOptionOnMenuOpen={false}
				onChange={handleChange}
				onInputChange={handleInputChange}
				onFocus={handleFocus}
				onBlur={handleBlur}
				onMenuOpen={handleMenuOpen}
				onMenuClose={handleMenuClose}
				onKeyDown={handleSelectKeyDown}
				styles={selectStyles}
				classNames={selectClassNames}
				components={selectComponents}
				className={filterClassName}
				classNamePrefix='react-select'
				menuPortalTarget={menuInPlace ? null : typeof document !== 'undefined' ? document.body : null}
				menuPosition={menuInPlace ? 'absolute' : 'fixed'}
				menuShouldScrollIntoView={false}
				noOptionsMessage={() => '无匹配项'}
				loadingMessage={() => '加载中，请稍候…'}
				isDisabled={isLoading || isEmptyOptions}
				aria-label={isLoading ? `${label}（加载中，请稍候）` : label}
			/>
		</div>
	);
};

export default OrderListMultiSelectFilter;
